import { useMemo } from 'react';
import { Lock, Unlock, BringToFront, SendToBack, Trash2 } from 'lucide-react';
import { useProjectStore } from '../../../state/projectStore';
import type { PageElement, PhotobookProject } from '@fotoboek/domain';
import { EmptyState } from '../../ui/EmptyState';
import { Label, Input } from '../../ui/Input';
import { PhotoProperties } from './PhotoProperties';
import { TextProperties } from './TextProperties';
import { ShapeProperties } from './ShapeProperties';
import { t } from '../../../i18n';
import './properties-panel.css';

function findElement(
  project: PhotobookProject | null,
  target: { scope: 'page'; pageId: string } | { scope: 'cover'; panel: 'front' | 'back' | 'spine' },
  elementId: string,
): PageElement | null {
  if (!project) return null;
  const elements =
    target.scope === 'page'
      ? project.pages.find((p) => p.id === target.pageId)?.elements
      : project.cover.panels[target.panel].elements;
  return elements?.find((e) => e.id === elementId) ?? null;
}

export function PropertiesPanel() {
  const project = useProjectStore((s) => s.project);
  const selection = useProjectStore((s) => s.selection);
  const updateElement = useProjectStore((s) => s.updateElement);
  const removeElement = useProjectStore((s) => s.removeElement);
  const moveElementZ = useProjectStore((s) => s.moveElementZ);
  const toggleElementLock = useProjectStore((s) => s.toggleElementLock);

  const element = useMemo(() => {
    const soleElementId = selection.elementIds.length === 1 ? selection.elementIds[0] : undefined;
    if (!selection.target || !soleElementId) return null;
    return findElement(project, selection.target, soleElementId);
  }, [project, selection]);

  if (!element || !selection.target) {
    return (
      <div className="properties-panel">
        <EmptyState title={t('editor.properties.empty')} />
      </div>
    );
  }

  const target = selection.target;
  const elementId = element.id;
  const update = (patch: Partial<PageElement>) => updateElement(target, elementId, patch);

  return (
    <div className="properties-panel scrollbar-thin">
      {element.type === 'photoFrame' ? (
        <PhotoProperties element={element} onChange={update} />
      ) : null}
      {element.type === 'text' ? <TextProperties element={element} onChange={update} /> : null}
      {element.type === 'shape' ? <ShapeProperties element={element} onChange={update} /> : null}

      <div className="properties-section">
        <div className="left-panel-section-title">{t('editor.properties.common.position')}</div>
        <div className="properties-row">
          <div className="field-group">
            <Label>X (mm)</Label>
            <Input
              type="number"
              value={Math.round(element.x)}
              onChange={(e) => update({ x: Number(e.target.value) })}
            />
          </div>
          <div className="field-group">
            <Label>Y (mm)</Label>
            <Input
              type="number"
              value={Math.round(element.y)}
              onChange={(e) => update({ y: Number(e.target.value) })}
            />
          </div>
        </div>
        <div className="left-panel-section-title">{t('editor.properties.common.size')}</div>
        <div className="properties-row">
          <div className="field-group">
            <Label>W (mm)</Label>
            <Input
              type="number"
              value={Math.round(element.width)}
              onChange={(e) => update({ width: Math.max(2, Number(e.target.value)) })}
            />
          </div>
          <div className="field-group">
            <Label>H (mm)</Label>
            <Input
              type="number"
              value={Math.round(element.height)}
              onChange={(e) => update({ height: Math.max(2, Number(e.target.value)) })}
            />
          </div>
        </div>
        <div className="field-group">
          <Label>{t('editor.properties.common.rotation')}</Label>
          <Input
            type="number"
            value={Math.round(element.rotation)}
            onChange={(e) => update({ rotation: Number(e.target.value) })}
          />
        </div>
        <div className="field-group">
          <Label>{t('editor.properties.common.opacity')}</Label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={element.opacity}
            onChange={(e) => update({ opacity: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="properties-actions">
        <button
          className="properties-icon-btn"
          onClick={() => moveElementZ(target, elementId, 'forward')}
          title={t('editor.properties.common.layerForward')}
        >
          <BringToFront size={16} />
        </button>
        <button
          className="properties-icon-btn"
          onClick={() => moveElementZ(target, elementId, 'backward')}
          title={t('editor.properties.common.layerBackward')}
        >
          <SendToBack size={16} />
        </button>
        <button
          className="properties-icon-btn"
          onClick={() => toggleElementLock(target, elementId)}
          title={
            element.locked
              ? t('editor.properties.common.unlock')
              : t('editor.properties.common.lock')
          }
        >
          {element.locked ? <Unlock size={16} /> : <Lock size={16} />}
        </button>
        <button
          className="properties-icon-btn danger"
          onClick={() => removeElement(target, elementId)}
          title={t('common.delete')}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
